import { isApiError, problemDetailText } from '../../services/apiError'
import { NOT_MEASURED, errorKind, errorStatus, errorSummary, whenLabel } from '../../utils/format'
import type { AdminGate } from '../../utils/adminGate'
import { signInRequiredText } from '../jobs/haltClearFormat'
import { RUN_CANCEL_NOTE_MAX_LENGTH } from '../../types/etlRunCancellation'
import type { EtlRunCancellation, EtlRunCancellationList } from '../../types/etlRunCancellation'

/**
 * Cancelling a running run (D6b(ii)), as pure decisions: which outcomes may be cancelled, whether the action is
 * enabled and why not, what each request status says, what the dialog says, and which error sentence to show.
 *
 * Pure on purpose — no React, no MSAL — so every word is pinned by cancelFormat.test.ts.
 *
 * THREE RULES run through it.
 * - A write FAILS CLOSED: the action is enabled only when the server says `canCancelRuns` (the gate is `admin`), the
 *   request table exists, and no request is already open. Every other state shows the action disabled, with why.
 * - Recorded is not cancelled: a 202 says the request was written. The request's status and the run's own outcome say
 *   whether anything stopped, and `ClosedNotRunning` — a record closed because nothing was performing it — is never
 *   called "Cancelled".
 * - An unobserved thing is never shown as fine: no request table is NOT_MEASURED, never "nobody asked".
 */

// ---- vocabulary --------------------------------------------------------------------------------------------

/** Where the server withheld who asked, or why, from this caller. Withheld is not absent. */
export const WITHHELD_TO_ADMINS = 'shown to administrators'

/** Why there is nothing to show when the request table does not exist here. Rendered after NOT_MEASURED. */
export const CANCEL_NOT_PROVISIONED = 'cancellation is not recorded in this database'

/** The migration that creates the request table, named wherever its absence is. */
export const CANCEL_MIGRATION = 'AddSignalHandlingAndRunCancellation'

/** The way out when Automaton could not deliver a stop. */
export const HANGFIRE_HAND_OFF = 'Stop it from the Hangfire dashboard.'

/** The note's bound, re-exported for the dialog. */
export const CANCEL_NOTE_MAX_LENGTH = RUN_CANCEL_NOTE_MAX_LENGTH

/** Whether a request is still open: `Requested` or `Delivered`. Anything else (or nothing) is not. */
export function isOpenCancelStatus(status: string | null | undefined): boolean {
  return status === 'Requested' || status === 'Delivered'
}

/** Whether a request has been decided. An absent request is not decided. */
export function isDecidedCancellation(request: Pick<EtlRunCancellation, 'status'> | null | undefined): boolean {
  return request != null && !isOpenCancelStatus(request.status)
}

/** The run outcomes a cancel may be asked for: running, and stale (Automaton decides whether anything holds it). */
export function isCancellableOutcome(outcome: string | null | undefined): boolean {
  return outcome === 'running' || outcome === 'stale'
}

// ---- the status --------------------------------------------------------------------------------------------

/**
 * What a request's status says, in one line.
 *
 * - Requested: "Waiting for Automaton".
 * - Delivered: "Stop sent to job 4711" (or "jobs 4711, 4712") — the jobs it was delivered to, which a resumed run
 *   makes different from its creating job.
 * - Completed: "Stopped — run is Cancelled". "Stopped" is said ONLY of a run recorded Cancelled: a Completed request
 *   whose run ended any other way (an older server decided Completed whatever the status) reads as FinishedBeforeStop.
 * - FinishedBeforeStop: "Run ended Succeeded before the stop took effect" — nothing was stopped by this request.
 * - AlreadyFinished: "Run had already finished" — nothing was cancelled.
 * - ClosedNotRunning: "Closed — no worker was running it". NEVER "Cancelled": nothing was running to cancel.
 * - Failed: Automaton's own reason, plus the hand-off to the Hangfire dashboard when the reason does not give it.
 * - A status this build does not know: shown as itself.
 */
export function cancelStatusLabel(
  request: Pick<EtlRunCancellation, 'status' | 'statusDetail' | 'deliveredJobIds' | 'runStatusAtDecision'>,
): string {
  const status = request.status as string
  switch (status) {
    case 'Requested':
      return 'Waiting for Automaton'
    case 'Delivered': {
      const ids = request.deliveredJobIds ?? []
      if (ids.length === 0) return 'Stop sent to Hangfire'
      return `Stop sent to job${ids.length === 1 ? '' : 's'} ${ids.join(', ')}`
    }
    case 'Completed':
      if (request.runStatusAtDecision === 'Cancelled') return 'Stopped — run is Cancelled'
      return finishedBeforeStopText(request.runStatusAtDecision)
    case 'FinishedBeforeStop':
      return finishedBeforeStopText(request.runStatusAtDecision)
    case 'AlreadyFinished':
      return 'Run had already finished'
    case 'ClosedNotRunning':
      return 'Closed — no worker was running it'
    case 'Failed': {
      const detail = request.statusDetail?.trim() || 'Nothing could be delivered; nothing was stopped.'
      return /hangfire dashboard/i.test(detail) ? detail : `${detail} ${HANGFIRE_HAND_OFF}`
    }
    default:
      return status.trim() || 'Unknown status'
  }
}

/** A delivered stop the run outran: never "Stopped". */
function finishedBeforeStopText(runStatus: string | null): string {
  return runStatus
    ? `Run ended ${runStatus} before the stop took effect`
    : 'Stop delivered; the run’s record has since been removed, so how it ended is not known'
}

/** A failed request in a badge-sized space; the full {@link cancelStatusLabel} goes beside it. */
export const CANCEL_FAILED_BRIEF = 'Not delivered — nothing was stopped'

/**
 * The status for a badge: {@link cancelStatusLabel}, except a failure, whose label carries Automaton's whole reason
 * and the hand-off — too long for a badge, so it is {@link CANCEL_FAILED_BRIEF} there and the label is shown in full
 * beside it.
 */
export function cancelStatusBrief(
  request: Pick<EtlRunCancellation, 'status' | 'statusDetail' | 'deliveredJobIds' | 'runStatusAtDecision'>,
): string {
  return request.status === 'Failed' ? CANCEL_FAILED_BRIEF : cancelStatusLabel(request)
}

/** The status badge's colour: open is blue, a failure red, every other decision neutral. */
export function cancelStatusColor(status: string): string {
  if (isOpenCancelStatus(status)) return 'blue'
  if (status === 'Failed') return 'red'
  return 'gray'
}

/** A value the server may withhold: itself, "shown to administrators" when withheld, else the unmeasured dash. */
export function withheldText(value: string | null | undefined, withheld: boolean | undefined): string {
  if (withheld) return WITHHELD_TO_ADMINS
  const text = value?.trim()
  return text ? text : NOT_MEASURED
}

/** "Requested 29/09/2026, 10:15 (3m ago) by Jane Doe" — or "by (shown to administrators)". */
export function requestedText(request: Pick<EtlRunCancellation, 'requestedAt' | 'requestedBy' | 'requestedByWithheld'>): string {
  const who = request.requestedByWithheld
    ? `(${WITHHELD_TO_ADMINS})`
    : request.requestedBy?.trim() || 'an unnamed administrator'
  return `Requested ${whenLabel(request.requestedAt)} by ${who}`
}

// ---- the action ------------------------------------------------------------------------------------------

/** The cancel-requests read, as much of it as the action and the card need. */
export interface CancellationsQuery {
  data?: EtlRunCancellationList
  isLoading: boolean
  isError: boolean
  error: unknown
}

export type CancelActionView =
  | { kind: 'hidden' }
  | { kind: 'enabled'; note: string }
  | { kind: 'disabled'; reason: string }

/** When the catalogue has not answered whether this session may cancel. */
export const GATE_PENDING_REASON = 'Checking whether this session may cancel a run…'

/** When the catalogue could not be read at all. */
export const GATE_UNKNOWN_REASON =
  'The job catalogue could not be loaded, so whether this session may cancel a run is not known. An administrator '
  + 'signed in with Entra and holding Lighthouse.Admin can cancel it once it loads.'

/** When the server refused without saying why. */
export const CANCEL_REFUSED_FALLBACK = 'The server says this session may not cancel a run.'

/** After the scope sentence, under an enabled action. */
export const CANCEL_ENABLED_NOTE = 'Asks why first; nothing happens until you confirm.'

/**
 * Whether to show the Cancel action, and whether it may be pressed.
 *
 * - HIDDEN unless the run's outcome is running or stale — a finished run has nothing to stop.
 * - ENABLED only when the gate is `admin` (the server's `canCancelRuns`), the request table exists, and no request is
 *   open — neither in the run's list nor the one this session just made.
 * - DISABLED, with the reason, in every other state: the gate pending, unknown or denied; the requests not yet read,
 *   unreadable, or not provisioned; a request already open. A write fails closed.
 *
 * Enabled, its note is what the cancel stops (`scope`, from the run's own detail) and that it asks first.
 */
export function cancelActionView(input: {
  runOutcome: string | null | undefined
  gate: AdminGate
  cancellations: CancellationsQuery
  /** The request this session just made, as last polled — open until it is decided. */
  submitted?: EtlRunCancellation | null
  /** What the cancel stops (runScopeText). */
  scope?: string
}): CancelActionView {
  const { runOutcome, gate, cancellations, submitted, scope } = input
  if (!isCancellableOutcome(runOutcome)) return { kind: 'hidden' }

  switch (gate.state) {
    case 'pending':
      return { kind: 'disabled', reason: GATE_PENDING_REASON }
    case 'unknown':
      return { kind: 'disabled', reason: GATE_UNKNOWN_REASON }
    case 'denied':
      return {
        kind: 'disabled',
        reason: `You cannot cancel a run from this session. ${gate.reason.trim() || CANCEL_REFUSED_FALLBACK}`,
      }
    case 'admin':
      break
    default:
      return { kind: 'disabled', reason: GATE_UNKNOWN_REASON }
  }

  if (submitted && isOpenCancelStatus(submitted.status)) {
    return { kind: 'disabled', reason: `A cancel request is already open: ${cancelStatusLabel(submitted)}.` }
  }

  const data = cancellations.data
  if (!data) {
    if (cancellations.isError) {
      if (errorKind(cancellations.error) === 'not-found') {
        return { kind: 'disabled', reason: 'This API does not offer run cancellation.' }
      }
      const summary = errorSummary(cancellations.error)
      return {
        kind: 'disabled',
        reason: "Could not read this run's cancel requests, so whether one is already open is not known."
          + (summary ? ` ${summary}` : ''),
      }
    }
    return { kind: 'disabled', reason: "Checking this run's cancel requests…" }
  }

  if (!data.provisioned) {
    return {
      kind: 'disabled',
      reason: `${NOT_MEASURED} ${CANCEL_NOT_PROVISIONED}: apply migration ${CANCEL_MIGRATION} to cancel runs from here.`,
    }
  }

  if (data.latest && isOpenCancelStatus(data.latest.status)) {
    return { kind: 'disabled', reason: `A cancel request is already open: ${cancelStatusLabel(data.latest)}.` }
  }

  return { kind: 'enabled', note: scope ? `${scope} ${CANCEL_ENABLED_NOTE}` : CANCEL_ENABLED_NOTE }
}

// ---- the run's scope -------------------------------------------------------------------------------------

/** The run detail's counts the scope sentence reads (ExecutionDetail / ExecutionListItem). */
export interface RunScopeCounts {
  totalEntitiesRecordedAtCreation: number
  entityRowCount: number
  entitiesSucceeded: number
  entitiesFailed: number
  entitiesSkipped: number
  /** Absent from an API older than the cancelled entity state: 0. */
  entitiesCancelled?: number
}

/**
 * What a cancel stops, in one sentence: always the WHOLE run, never one entity.
 *
 * - From an entity (the Explorer): "Stops the whole run ETL-…, including 55 other entity loads (12 already finished,
 *   which stay merged)." — the others counted from the run's own detail: the larger of what it was created for and
 *   the rows it holds (a resumed run can hold more), less this one.
 * - From the run (Execution History): "Stops the whole run ETL-… — all 56 entity loads in it (…)".
 * - Without the run's detail (the Workers page): "Stops the whole run ETL-… — every entity in it."
 */
export function runScopeText(runKey: string, counts: RunScopeCounts | null | undefined, fromEntity: boolean): string {
  if (!counts) return `Stops the whole run ${runKey} — every entity in it.`

  const total = Math.max(counts.totalEntitiesRecordedAtCreation, counts.entityRowCount)
  const finished = counts.entitiesSucceeded + counts.entitiesFailed + counts.entitiesSkipped + (counts.entitiesCancelled ?? 0)
  const finishedNote = finished > 0 ? ` (${finished} already finished, which stay merged)` : ''

  if (fromEntity) {
    const others = total - 1
    if (others <= 0) return `Stops the whole run ${runKey}; it holds no other entity load.`
    return `Stops the whole run ${runKey}, including ${others} other entity load${others === 1 ? '' : 's'}${finishedNote}.`
  }

  if (total <= 1) return `Stops the whole run ${runKey} — its one entity load.`
  return `Stops the whole run ${runKey} — all ${total} entity loads in it${finishedNote}.`
}

// ---- the dialog ------------------------------------------------------------------------------------------

/**
 * How fast, and where, a cancel lands. Verified against the code: EtlRunCancelWorker polls every 5 s; Hangfire's
 * ServerJobCancellationWatcher checks every CancellationCheckInterval (5 s by default, not overridden in Automaton's
 * Program.cs) and cancels the job's token; ChunkedEltRunner checks it before each chunk and each transform batch, and
 * SqlBulkCopy honours it per row. A synchronous Oracle read in progress does not observe it.
 */
export function cancelTimingText(runKey: string): string {
  return `Stops the whole run ${runKey} — every entity in it — usually within about 10 seconds (Automaton checks every `
    + '5 s; Hangfire delivers the stop within 5 s), at the next chunk, transform batch or bulk-copy boundary; a long '
    + 'Oracle read can delay it.'
}

/**
 * What stays and what goes. Each transform batch merges and deletes what it consumed in one transaction, so a merged
 * batch is committed. Every unfinished chunk of the run — the one in flight, and any an earlier attempt left — has its
 * unmerged landed rows deleted by its chunk id: at once for the entity that was running (ChunkedEltRunner), and for
 * every other entity, or where that delete failed, by that entity's next load before it lands anything
 * (DiscardCancelledLeftoversAsync) — so no transform ever merges them. The watermark is not advanced, so a later load
 * over the same window redoes it idempotently.
 */
export const CANCEL_KEEPS_TEXT =
  'Batches already merged stay merged; nothing is rolled back. Every unfinished chunk of the run has its rows not yet '
  + 'merged deleted from landing — at once for the entity that was running, and otherwise before that entity next '
  + 'loads — so none of them is ever merged. The watermark does not move, so a later load over the same window redoes it.'

/**
 * Why a cancel is final. EtlRunTelemetry.BeginRunAsync resumes only the newest Running-or-Failed run of the same
 * orchestrator and mode inside the resume window; Cancelled is excluded, so the cancelled run is never reopened.
 */
export const CANCEL_FINAL_TEXT =
  'The run is recorded Cancelled and is never resumed: the next load either reopens an earlier failed run — or one a '
  + 'dead worker left Running — still inside the resume window, or starts its window from the beginning.'

/**
 * What this action cannot reach. A load interrupted by a worker restart is recorded Failed — resumable — and Hangfire
 * requeues it, so until a worker picks it up again nothing is running to stop; a queued request has no run yet.
 */
export const CANCEL_UNREACHABLE_TEXT =
  'A load interrupted by a worker restart, or a request still queued, cannot be stopped here until it is running.'

/**
 * For a stale run: Automaton decides from Hangfire's own record, never from staleness. A run it closes has nothing in
 * flight to discard at once, so its unfinished chunks' landed rows are deleted by each entity's next load, before it
 * lands anything.
 */
export const CANCEL_STALE_TEXT =
  'This run is stale — no heartbeat within the worker\'s rule. Automaton asks Hangfire whether a live job still holds '
  + 'it: if one does, it is stopped as above; if no live job holds it, Automaton closes it, recorded Cancelled, with '
  + 'nothing to stop — and the rows its unfinished chunks landed are deleted before each entity next loads.'

/** Every paragraph of the dialog's explanation, in order. */
export function cancelModalCopy(runKey: string, stale: boolean): string[] {
  return [
    cancelTimingText(runKey),
    CANCEL_KEEPS_TEXT,
    CANCEL_FINAL_TEXT,
    ...(stale ? [CANCEL_STALE_TEXT] : []),
    CANCEL_UNREACHABLE_TEXT,
  ]
}

/** A note is required, and at most the server's bound once trimmed. */
export function cancelConfirmEnabled(note: string): boolean {
  const trimmed = note.trim()
  return trimmed !== '' && trimmed.length <= CANCEL_NOTE_MAX_LENGTH
}

// ---- the answer ------------------------------------------------------------------------------------------

export interface CancelToast {
  title: string
  message: string
  color: string
}

/**
 * What to say once the server answered. A repeat is not a success: someone else asked first, and this note was NOT
 * recorded. Neither says the run stopped — that is the request's status and the run's outcome, which the page follows.
 */
export function cancelToast(response: EtlRunCancellation): CancelToast {
  if (!response.acceptedByThisRequest) {
    const who = response.requestedByWithheld
      ? 'an administrator'
      : response.requestedBy?.trim() || 'an administrator'
    return {
      title: 'Already requested',
      message: `A cancel of run ${response.runKey} was already requested by ${who} at ${whenLabel(response.requestedAt)}; `
        + 'your note was not recorded.',
      color: 'yellow',
    }
  }
  return {
    title: 'Cancel requested',
    // Recorded is not acted on: Automaton may yet find nothing it can stop (the dashboard hand-off), so no promise.
    message: `Recorded for run ${response.runKey}; Automaton has not acted on it yet. This page follows the request until `
      + 'it is decided.',
    color: 'blue',
  }
}

/**
 * Why a cancel request failed, for the dialog — which stays open with the note intact. Each status has its own words;
 * every refusal leaves the run running and records nothing.
 *
 * - not sent / 401: sign in with Entra (the shared API key is refused even locally).
 * - 400: the server's own words (no note; too long).
 * - 403: the server's detail first — the identity refusal names it — else the missing role.
 * - 404: no such run.
 * - 409: the run is no longer running, with its status.
 * - 503: the request table is not provisioned — names the migration.
 * - other 5xx / network: unknown whether it was recorded; a retry is safe, because a repeat finds the open request.
 */
export function cancelErrorText(error: unknown): string {
  const signIn = signInRequiredText(error)
  if (signIn) return signIn

  const detail = isApiError(error) ? problemDetailText(error.body) : null

  if (errorStatus(error) === 503) {
    const named = detail && detail.includes(CANCEL_MIGRATION) ? ` ${detail}` : ` Apply migration ${CANCEL_MIGRATION}.`
    return `Run cancellation is not provisioned in this database; nothing was requested.${named}`
  }

  switch (errorKind(error)) {
    case 'bad-request':
      return detail ?? errorSummary(error) ?? 'The server refused the note.'
    case 'forbidden':
      return detail ?? 'Cancelling a run requires the Lighthouse.Admin app role, which this account does not hold.'
    case 'not-found':
      return 'No such run: it may have passed its retention window. Nothing was requested.'
    case 'conflict':
      return `The run is no longer running: ${notRunningStatus(error) ?? 'its status was not given'}. Nothing was requested.`
    case 'server':
    case 'network':
      return 'Could not request the cancel: unknown whether it was recorded — retrying is safe; a repeat finds the open '
        + 'request and records nothing.'
    default:
      return errorSummary(error) ?? 'Could not request the cancel.'
  }
}

/** The run's status from a 409's EtlRunNotRunning body (its `status` is a name, not the HTTP status). */
function notRunningStatus(error: unknown): string | null {
  if (!isApiError(error)) return null
  const body = error.body
  if (typeof body !== 'object' || body === null) return null
  const status = (body as Record<string, unknown>).status
  return typeof status === 'string' && status.trim() ? status.trim() : null
}

// ---- the card --------------------------------------------------------------------------------------------

export type CancellationCardView =
  | { kind: 'hidden' }
  | { kind: 'loading' }
  | { kind: 'failed'; summary: string | null }
  | { kind: 'not-offered' }
  | { kind: 'unprovisioned' }
  | { kind: 'none' }
  | { kind: 'request'; latest: EtlRunCancellation; earlier: number }

/**
 * What the run's cancellation card shows. Loading, failed, not offered, unprovisioned, none and a request all look
 * different. `live` is whether the run can still be cancelled (running or stale): only then is "nothing asked yet"
 * worth a card, and only then is a loading read shown rather than nothing.
 */
export function cancellationCardView(query: CancellationsQuery, live: boolean): CancellationCardView {
  const data = query.data
  if (data) {
    if (!data.provisioned) return live ? { kind: 'unprovisioned' } : { kind: 'hidden' }
    if (data.latest) {
      return { kind: 'request', latest: data.latest, earlier: Math.max(0, (data.items?.length ?? 1) - 1) }
    }
    return live ? { kind: 'none' } : { kind: 'hidden' }
  }
  if (query.isError) {
    if (errorKind(query.error) === 'not-found') return live ? { kind: 'not-offered' } : { kind: 'hidden' }
    return { kind: 'failed', summary: errorSummary(query.error) }
  }
  return live ? { kind: 'loading' } : { kind: 'hidden' }
}

/**
 * The run's outcome now, as the card says it: from the served `runOutcome`, or "no longer recorded" when the run is
 * gone. A ClosedNotRunning request's run is recorded Cancelled, and the card says it was closed, not stopped.
 */
export function runNowText(request: Pick<EtlRunCancellation, 'status' | 'runOutcome'>): string | null {
  if (request.runOutcome === null) return 'The run is no longer recorded.'
  if (request.status === 'ClosedNotRunning') {
    return 'Closed by Automaton: no worker was performing it, so nothing was stopped.'
  }
  return null
}
